import { css } from 'lit';

export const widgetStyles = css`
  :host {
    /* Design tokens — see spec §5.1. */
    --sa-bg-primary: #282828;
    --sa-bg-panel: #181818;
    --sa-border-subtle: #404040;
    --sa-track: #333333;
    --sa-text-primary: #e0e0e0;
    --sa-text-secondary: #b0b0b0;
    --sa-text-muted: #808080;
    --sa-button-inactive-bg: #252525;
    --sa-button-inactive-fg: #808080;
    --sa-button-active-bg: rgb(100, 200, 140);
    --sa-button-active-fg: #141414;
    --sa-waveform-unplayed: #6a6a6a;
    --sa-waveform-played: #b0b0b0;
    --sa-playhead: #ffffff;
    --sa-stem-voice: #66bb6a;
    --sa-stem-voice-bright: #6d9c70;
    --sa-stem-music: #4a90d9;
    --sa-stem-effects: #e89850;
    --sa-stem-other: #ab7ac4;
    --sa-stem-drums: #4a90d9;
    --sa-stem-bass: #e89850;
    --sa-focus-ring: #66bb6a;
    --sa-dropdown-bg: #202020;

    display: block;
    container-type: inline-size;
    container-name: widget;
    width: 100%;
    max-width: 1104px;

    font-family: -apple-system, BlinkMacSystemFont, 'Inter', 'Segoe UI', Roboto, system-ui,
      sans-serif;
    color: var(--sa-text-primary);

    /* Prevent text selection from interfering with scrub/drag. */
    -webkit-user-select: none;
    user-select: none;
  }

  .too-narrow {
    display: none;
    align-items: center;
    justify-content: center;
    width: 100%;
    min-height: 120px;
    background: var(--sa-bg-primary);
    border-radius: 8px;
    color: var(--sa-text-secondary);
    font-size: 14px;
    text-align: center;
    padding: 24px;
    box-sizing: border-box;
  }

  .card {
    display: grid;
    grid-template-columns: 1fr 280px;
    gap: 12px;
    width: 100%;
    box-sizing: border-box;
    padding: 20px 20px 24px;
    background: var(--sa-bg-primary);
    border-radius: 8px;
  }

  .left {
    display: flex;
    flex-direction: column;
    min-width: 0;
    min-height: 0;
    position: relative;
  }

  .waveform-host {
    flex: 1 1 auto;
    position: relative;
    min-height: 160px;
  }

  .playhead {
    position: absolute;
    top: 0;
    left: 0;
    width: 1px;
    height: 100%;
    background: var(--sa-playhead);
    pointer-events: none;
    opacity: 0;
  }

  .waveform-loading {
    position: absolute;
    inset: 0;
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 10px;
    pointer-events: none;
    color: var(--sa-text-secondary);
    font-size: 13px;
    letter-spacing: 0.01em;
    /* Soft scrim so the indicator stays legible on top of a stale waveform
       (model switch keeps the previous peaks painted until new ones land). */
    background: rgba(40, 40, 40, 0.78);
  }
  .waveform-loading-label {
    line-height: 1;
  }

  .waveform-host {
    overflow: hidden;
  }

  .transport {
    display: flex;
    align-items: center;
    gap: 12px;
    height: 48px;
    padding-top: 8px;
  }

  .play-btn {
    background: transparent;
    border: none;
    padding: 0;
    width: 40px;
    height: 40px;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    color: var(--sa-text-primary);
    border-radius: 4px;
  }
  .play-btn:hover {
    color: #fff;
  }
  .play-btn:focus-visible {
    outline: 2px solid var(--sa-focus-ring);
    outline-offset: 2px;
  }
  .play-btn[disabled] {
    cursor: default;
    opacity: 0.5;
  }

  .timecode {
    font-size: 13px;
    font-family: ui-monospace, 'SF Mono', Menlo, Consolas, monospace;
    color: var(--sa-text-secondary);
    letter-spacing: 0.02em;
  }

  /* ---- Right panel ---- */

  .panel {
    background: var(--sa-bg-panel);
    border-radius: 4px;
    padding: 12px 14px 18px;
    box-sizing: border-box;
    display: flex;
    flex-direction: column;
    min-width: 0;
  }

  .model-label {
    font-size: 12.6px;
    color: var(--sa-text-secondary);
    margin: 0 0 6px;
  }

  .model-select-wrap {
    position: relative;
    margin-bottom: 12px;
  }

  .model-select {
    appearance: none;
    -webkit-appearance: none;
    width: 100%;
    height: 27px;
    padding: 0 26px 0 8px;
    background: var(--sa-dropdown-bg);
    color: var(--sa-text-primary);
    border: 1px solid var(--sa-border-subtle);
    border-radius: 3px;
    font-family: inherit;
    font-size: 14px;
    cursor: pointer;
  }
  .model-select:focus-visible {
    outline: 2px solid var(--sa-focus-ring);
    outline-offset: 2px;
  }
  /* Safari (and some Chrome versions) keep :focus-visible on form controls
     and tabindex="0" elements after a mouse click, which leaves the green ring
     stuck until another click. Suppress every focus indicator inside the
     widget unless the user's last input was keyboard. Keyboard navigation
     still gets full focus rings for WCAG AA compliance. */
  :host([data-modality='mouse']) .model-select:focus,
  :host([data-modality='mouse']) .model-select:focus-visible,
  :host([data-modality='mouse']) canvas.waveform:focus,
  :host([data-modality='mouse']) canvas.waveform:focus-visible,
  :host([data-modality='mouse']) .play-btn:focus,
  :host([data-modality='mouse']) .play-btn:focus-visible,
  :host([data-modality='mouse']) .sm-btn:focus,
  :host([data-modality='mouse']) .sm-btn:focus-visible {
    outline: none !important;
    box-shadow: none !important;
  }
  :host([data-modality='mouse']) .slider:focus::-webkit-slider-thumb,
  :host([data-modality='mouse']) .slider:focus-visible::-webkit-slider-thumb {
    outline: none !important;
    box-shadow: none !important;
  }
  :host([data-modality='mouse']) .slider:focus::-moz-range-thumb,
  :host([data-modality='mouse']) .slider:focus-visible::-moz-range-thumb {
    outline: none !important;
    box-shadow: none !important;
  }

  .model-select-chevron {
    position: absolute;
    right: 8px;
    top: 50%;
    transform: translateY(-50%);
    pointer-events: none;
    color: var(--sa-text-secondary);
  }

  .stems {
    display: flex;
    flex-direction: column;
    gap: 0;
    /* Reserve vertical room for 4 stem rows (50px each) so the widget's
       overall height stays consistent across models that have fewer stems. */
    min-height: 200px;
  }

  .stem-row {
    height: 50px;
    display: flex;
    flex-direction: column;
    justify-content: flex-start;
  }

  .stem-label {
    font-size: 15px;
    color: var(--sa-text-primary);
    line-height: 16px;
    margin-bottom: 2px;
  }

  .stem-controls {
    display: grid;
    grid-template-columns: 1fr 52px 23px 23px;
    align-items: center;
    gap: 6px;
    height: 23px;
  }

  .stem-row.dimmed .stem-label,
  .stem-row.dimmed .slider,
  .stem-row.dimmed .stem-value {
    opacity: 0.4;
  }

  /* ---- Slider ---- */

  .slider {
    appearance: none;
    -webkit-appearance: none;
    background: transparent;
    width: 100%;
    height: 23px;
    margin: 0;
    padding: 0;
    cursor: pointer;
  }
  .slider:focus-visible {
    outline: none;
  }
  .slider:focus-visible::-webkit-slider-thumb {
    outline: 2px solid var(--sa-focus-ring);
    outline-offset: 2px;
  }
  .slider:focus-visible::-moz-range-thumb {
    outline: 2px solid var(--sa-focus-ring);
    outline-offset: 2px;
  }

  .slider::-webkit-slider-runnable-track {
    height: 4px;
    background: var(--sa-track);
    border-radius: 2px;
  }
  .slider::-moz-range-track {
    height: 4px;
    background: var(--sa-track);
    border-radius: 2px;
  }

  .slider::-webkit-slider-thumb {
    appearance: none;
    -webkit-appearance: none;
    width: 16px;
    height: 16px;
    background: var(--stem-color, #888);
    border-radius: 4px;
    border: none;
    margin-top: -6px; /* centers 16px thumb on 4px track */
    cursor: pointer;
  }
  .slider::-moz-range-thumb {
    width: 16px;
    height: 16px;
    background: var(--stem-color, #888);
    border-radius: 4px;
    border: none;
    cursor: pointer;
  }

  .stem-value {
    font-size: 12.6px;
    color: var(--sa-text-primary);
    background: transparent;
    border: 1px solid var(--sa-border-subtle);
    border-radius: 2px;
    height: 23px;
    line-height: 21px;
    text-align: center;
    font-family: inherit;
    padding: 0 4px;
    box-sizing: border-box;
  }

  /* ---- S / M buttons ---- */

  .sm-btn {
    width: 23px;
    height: 23px;
    background: var(--sa-button-inactive-bg);
    color: var(--sa-button-inactive-fg);
    border: 1px solid var(--sa-border-subtle);
    border-radius: 3px;
    padding: 0;
    font-size: 11px;
    font-family: inherit;
    font-weight: 600;
    cursor: pointer;
    line-height: 21px;
    text-align: center;
  }
  .sm-btn:hover {
    background: #2c2c2c;
  }
  .sm-btn:focus-visible {
    outline: 2px solid var(--sa-focus-ring);
    outline-offset: 1px;
  }
  .sm-btn[aria-pressed='true'] {
    background: var(--sa-button-active-bg);
    color: var(--sa-button-active-fg);
    border-color: var(--sa-button-active-bg);
  }

  /* ---- Waveform canvas ---- */

  canvas.waveform {
    display: block;
    position: absolute;
    inset: 0;
    width: 100%;
    height: 100%;
    cursor: pointer;
    /* Removes the legacy ~300ms tap-delay some mobile browsers apply while
       waiting to see if a tap becomes a double-tap-to-zoom. Taps fire
       immediately and scrubbing feels responsive on phones. */
    touch-action: manipulation;
  }
  canvas.waveform:focus-visible {
    outline: 2px solid var(--sa-focus-ring);
    outline-offset: 2px;
    border-radius: 4px;
  }

  /* ---- Loading / error states ---- */

  .loading-overlay {
    position: absolute;
    inset: 0;
    display: flex;
    align-items: center;
    justify-content: center;
    pointer-events: none;
  }

  .spinner {
    width: 20px;
    height: 20px;
    border: 2px solid rgba(255, 255, 255, 0.2);
    border-top-color: var(--sa-text-primary);
    border-radius: 50%;
    animation: spin 0.8s linear infinite;
  }

  @keyframes spin {
    to {
      transform: rotate(360deg);
    }
  }

  .stems.dimmed .stem-row {
    opacity: 0.5;
    pointer-events: none;
  }

  .error-panel {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: 12px;
    width: 100%;
    height: 100%;
    padding: 24px;
    box-sizing: border-box;
    text-align: center;
  }
  .error-panel p {
    margin: 0;
    color: var(--sa-text-primary);
    font-size: 14px;
  }
  .error-panel .error-actions {
    display: flex;
    gap: 12px;
    align-items: center;
  }
  .error-panel button {
    background: var(--sa-dropdown-bg);
    color: var(--sa-text-primary);
    border: 1px solid var(--sa-border-subtle);
    border-radius: 3px;
    padding: 6px 14px;
    font-family: inherit;
    font-size: 13px;
    cursor: pointer;
  }
  .error-panel button:hover {
    background: #2c2c2c;
  }
  .error-panel a {
    color: var(--sa-text-secondary);
    font-size: 13px;
  }

  /* Screen-reader-only live region. */
  .sr-only {
    position: absolute;
    width: 1px;
    height: 1px;
    padding: 0;
    margin: -1px;
    overflow: hidden;
    clip: rect(0, 0, 0, 0);
    white-space: nowrap;
    border: 0;
  }

  /* Reduced motion — disable spinner + waveform smoothness. */
  @media (prefers-reduced-motion: reduce) {
    .spinner {
      animation: none;
    }
  }

  /* ----- Responsive rules ---------------------------------------------------
     These must be declared AFTER the base .card / .panel / .waveform-host
     rules so they override on cascade. Media queries are used instead of
     container queries because :host-container queries don't fire reliably
     across browsers; the widget's width ≈ viewport width on mobile anyway. */

  /* Below the minimum usable width, show a placeholder instead of the widget. */
  @media (max-width: 279px) {
    .card {
      display: none;
    }
    .too-narrow {
      display: flex;
    }
  }

  /* Mobile / narrow reflow: stack waveform on top, stem panel beneath. */
  @media (max-width: 640px) {
    .card {
      grid-template-columns: 1fr;
      grid-template-rows: auto auto;
      padding: 12px 12px 14px;
      gap: 8px;
    }
    .waveform-host {
      min-height: 120px;
    }
    .panel {
      padding: 10px 12px 12px;
    }
    .stems {
      min-height: 0;
    }
  }
`;
